import React from "react";
import PropTypes from "prop-types";
import { Routes as ReactRouterRoutes, Route, Navigate } from "react-router";
import Candidate from "pages/candidate";
import CandidateHome from "pages/candidate/home";
import CandidateProfile from "pages/candidate/profile";
import Mission from "pages/missions/mission";
import MissionProvider from "pages/missions/mission/provider";
import Missions from "pages/missions";
import MissionsHome from "pages/missions/home";
import MissionsApplicants from "pages/missions/applicants";
import MissionsApplications from "pages/missions/applications";
import MissionsApplication from "pages/missions/applications/[id]";
import MissionBoard from "pages/missions/board";
import MissionNew from "pages/missions/new";
import MissionEdit from "pages/missions/mission/edit";
import Signup from "pages/signup";
import ClientSignup from "pages/clientSignup";
import SignalSignup from "pages/signal_signup";
import ProfileProvider from "pages/profiles/provider";
import Profiles from "pages/profiles";
import Rate from "pages/profiles/rate";
import Timeline from "pages/profiles/timeline";
import Notes from "pages/profiles/notes";
import ProfileVideoInterviews from "pages/profiles/video_interviews";
import ProfileSettings from "pages/profiles/settings";
import Partners from "pages/partners";
import PublicProfile from "pages/PublicProfile";
import Privacy from "pages/legal/privacy";
import TermsAndConditions from "pages/legal/termsAndConditions";
import Home from "pages/home";
import TimeCard from "pages/TimeCard";
import Timesheet from "pages/timesheet";
import Signin from "pages/signin";
import OTP from "pages/otp";
import ForgotPassword from "pages/forgot_password";
import ResetPassword from "pages/reset_password";
import SetPassword from "pages/set_password";
import Dashboard from "pages/dashboard";
import DashboardTeamView from "pages/dashboard/[id]";
import ClientFeedback from "pages/squads/squad/client_feedback";
import MissionProposal from "pages/missions/mission/proposal";
import Squads from "pages/squads";
import SquadNew from "pages/squads/new";
import SquadsHome from "pages/squads/home";
import SquadProfile from "pages/squads/squad/profile";
import SquadPulse from "pages/squads/squad/pulse";
import EffortReport from "pages/squads/squad/effort_report";
import SquadSettings from "pages/squads/squad/settings";
import Engineering from "pages/squads/squad/engineering";
import EngineeringCodeTelemetry from "pages/squads/squad/engineering/code_telemetry";
import DailyReport from "pages/squads/squad/daily_report";
import SquadActivityLog from "pages/squads/squad/engineering/activity_log";
import SquadLeaderboard from "pages/squads/squad/engineering/all_time_activity";
import SettingsCodeChallenges from "pages/settings/code_challenges";
import PlatformAccess from "pages/settings/platform_access";
import MissionEvents from "pages/settings/mission_events";
import SideNav from "components/Nav/SideNav";
import Network from "pages/network";
import Organizations from "pages/organizations";
import Lists from "pages/lists";
import List from "pages/lists/[slug]";
import PublicList from "pages/publicLists/[slug]";
import { AuthProvider, useAuth, useLinks, useViewport } from "hooks";
import { USER_TYPES } from "constants/index";
import DashboardTeamActivityLog from "pages/dashboard/[id]/activity_log";
import ClientHome from "pages/client/home";
import ClientCommunity from "pages/client/community";
import ClientApplications from "pages/client/applicants";
import Favorites from "pages/client/favorites";
import Favorite from "pages/client/favorites/[slug]";
import ClientTalentSearch from "pages/client/search";
import ClientTalentTeamBuilder from "pages/client/team_builder";
import ClientProfile from "pages/client/profile";
import ClientMissionBoard from "pages/client/mission_board";
import PublicMissionRole from "pages/missions/mission/public";
import TermSheets from "pages/squads/squad/term_sheet";
import BookMeeting from "pages/client/book_meeting";
import FeaturedTeams from "pages/client/featured_teams";
import PlatformSettings from "pages/settings";
import FeaturedTeamProfiles from "pages/client/featured_teams/[slug]";
import OrganizationProfile from "pages/organizations/profile";
import MobileMessage from "components/MobileMessage";
import ShowcaseSignup from "pages/showcaseSignup";
import SquadInsights from "pages/squads/squad/insights";
import LegalModal from "pages/legal/modal";
import Signal from "pages/squads/squad/settings/signal";
import SignalSettings from "pages/squads/squad/settings/signal_settings";
import DashboardActivityLogs from "pages/dashboard/activity_logs";
import Overview from "pages/dashboard/overview";
import ProfileCodeChallenges from "pages/profiles/code_challenges";
import ProfileApplications from "pages/profiles/applications";
import ProfileAssignments from "pages/profiles/assignments";
import DailyReportSettings from "pages/squads/squad/settings/daily_report_settings";
import GeneralSettings from "pages/squads/squad/settings/general_settings";
import MissionRole from "pages/missions/mission/role";
import MissionRoleDetails from "pages/missions/mission/role/details";
import MissionRoleRecommendations from "pages/missions/mission/role/recommendations";
import MissionRoleApplications from "pages/missions/mission/role/applications";
import Roles from "pages/missions/roles";
import ResendConfirmation from "pages/signup/send_confirmation";
import MissionRoleDiscussions from "pages/missions/mission/role/discussions";
import MissionRoleApplicantPreview from "pages/missions/applications/preview/[id]";
import MissionRoleTimeline from "pages/missions/mission/role/timeline";
import ClientMissionRoleApplications from "pages/client/role/applications";
import VideoPresentationSettings from "pages/settings/video_presentations";
import VideoPresentationSetting from "pages/settings/video_presentations/uuid";
import InterviewResponse from "pages/interview_responses/[uuid]";
import { InterviewLayoutSwitcher } from "components/VideoPresentation";
import LegacyTermSheet from "pages/squads/squad/legacy_term_sheet";
import MissionRoleVetting from "pages/missions/mission/role/vetting";
import TagManager from "pages/settings/tags";
import BlueprintSettings from "pages/settings/blueprints";
import BlueprintSetting from "pages/settings/blueprints/uuid";
import TalentSimpleHome from "pages/talent_simple/home";
import TalentSimpleApplications from "pages/talent_simple/applicants";
import TalentSimpleFavorites from "pages/talent_simple/favorites";
import TalentSimpleFavorite from "pages/talent_simple/favorites/[slug]";
import TalentSimpleTalentSearch from "pages/talent_simple/search";
import TalentSimpleTalentTeamBuilder from "pages/talent_simple/team_builder";
import TalentSimpleProfile from "pages/talent_simple/profile";
import TalentSimpleMissionBoard from "pages/talent_simple/mission_board";
import TalentSimpleBookMeeting from "pages/talent_simple/book_meeting";
import TalentSimpleFeaturedTeams from "pages/talent_simple/featured_teams";
import TalentSimpleFeaturedTeamProfiles from "pages/talent_simple/featured_teams/[slug]";
import TalentSimpleMissionRoleApplications from "pages/talent_simple/role/applications";
import PortalPage from "./PortalPage";
import UserSettings from "pages/user_settings";
import TalentSimpleUserSettings from "pages/talent_simple/user_settings";
import { AccountSettings, InterviewScheduling } from "components/Settings/Users";

const ROLE_BOARDS = {
  client: <PortalPage missionDev={<ClientMissionBoard />} talentSimple={<TalentSimpleMissionBoard />} />,
  default: <MissionBoard />,
};

const ROLE_DISCUSSION_ROUTES = (links) => ({
  client: <Route path={links.client_mission_role_discussions(`:id`, `:roleId`)} element={<MissionRoleDiscussions />} />,
});

/**
 * Routes
 *
 * @param {Object} flash - Flash message from Rails (passed via AppWithRouter)
 */
const Routes = ({ flash }) => {
  const links = useLinks();

  return (
    <ReactRouterRoutes>
      <Route path={links.signup} element={<Signup />} />
      <Route path={links.resend_confirmation} element={<ResendConfirmation />} />
      <Route path={links.client_signup} element={<ClientSignup />} />
      <Route path={links.signal_signup} element={<SignalSignup />} />
      <Route path={links.showcase_signup} element={<ShowcaseSignup />} />
      <Route path={links.login} element={<Signin flash={flash} />} />
      <Route path={links.otp} element={<OTP />} />
      <Route path={links.forgot_password} element={<ForgotPassword />} />
      <Route path={links.reset_password(`:id`)} element={<ResetPassword />} />
      <Route path={links.set_password(`:id`)} element={<SetPassword />} />
      <Route path={links.public_profile(`:id`)} element={<PublicProfile />} />
      <Route path={links.applicant_preview(`:uuid`)} element={<MissionRoleApplicantPreview />} />
      <Route path={links.public_mission_role(`:missionId`, `:id`)} element={<PublicMissionRole />} />
      <Route path={links.privacy} element={<Privacy />} />
      <Route path={links.terms_and_conditions} element={<TermsAndConditions />} />
      <Route path={links.mission_url(`:id`)} element={<MissionProposal />} />
      <Route path={links.public_list(`:id`)} element={<PublicList />} />
      <Route path="/" element={<AuthProvider />}>
        <Route path="/" element={<LegalModal />}>
          <Route path={links.candidate} element={<Candidate />}>
            <Route path={links.candidate_home} element={<CandidateHome />} />
            <Route path={links.candidate_profile} element={<CandidateProfile />} />
          </Route>
          <Route element={<InterviewLayoutSwitcher />}>
            <Route path={links.candidate_interview_response(":uuid")} element={<InterviewResponse />} />
            <Route path={links.profile_video_interview_response(":slug", ":uuid")} element={<InterviewResponse />} />
          </Route>
          <Route path="/" element={<SideNav />}>
            {[USER_TYPES.client, USER_TYPES.recruiter, USER_TYPES.partner, USER_TYPES.freelancer]
              .map((r) => r.toLowerCase())
              .map((role) => (
                <>
                  <Route path={links[`${role}_mission`]?.(`:id`)} element={<MissionProvider />}>
                    <Route path={links[`${role}_mission`]?.(`:id`)} element={<Mission />} />
                    <Route path={links[`${role}_mission_role`]?.(`:id`, `:roleId`)} element={<MissionRole />}>
                      <Route
                        path={links[`${role}_mission_role`]?.(`:id`, `:roleId`)}
                        element={<MissionRoleDetails />}
                      />
                    </Route>
                    {ROLE_DISCUSSION_ROUTES(links)?.[role] && (
                      <Route
                        path={links[`${role}_mission_role_discussions`]?.(`:id`, `:roleId`)}
                        element={<MissionRole />}
                      >
                        {ROLE_DISCUSSION_ROUTES(links)?.[role]}
                      </Route>
                    )}
                    {links[`${role}_mission_role_vetting`]?.(`:id`, `:roleId`) && (
                      <Route path={links[`${role}_mission_role_vetting`]?.(`:id`, `:roleId`)} element={<MissionRole />}>
                        <Route
                          path={links[`${role}_mission_role_vetting`]?.(`:id`, `:roleId`)}
                          element={<MissionRoleVetting />}
                        />
                      </Route>
                    )}
                  </Route>
                  <Route path={links[`${role}_missions`]} element={<Missions />}>
                    <Route path={links[`${role}_mission_board`]} element={ROLE_BOARDS[role] || ROLE_BOARDS.default} />
                  </Route>
                </>
              ))}
            <Route
              path={links.client_home}
              element={
                <ProtectedAdminRoute
                  element={<PortalPage missionDev={<ClientHome />} talentSimple={<TalentSimpleHome />} />}
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route
              path={links.client_search}
              element={
                <ProtectedAdminRoute
                  element={
                    <PortalPage missionDev={<ClientTalentSearch />} talentSimple={<TalentSimpleTalentSearch />} />
                  }
                  byPassFields={[`isClient`, `isShowcase`, `isSignalClient`]}
                />
              }
            />
            <Route
              path={links.favorites}
              element={
                <ProtectedAdminRoute
                  element={<PortalPage missionDev={<Favorites />} talentSimple={<TalentSimpleFavorites />} />}
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route
              path={links.favorite(`:id`)}
              element={
                <ProtectedAdminRoute
                  element={<PortalPage missionDev={<Favorite />} talentSimple={<TalentSimpleFavorite />} />}
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route
              path={links.user_settings}
              element={
                <ProtectedAdminRoute
                  element={
                    <PortalPage
                      missionDev={<UserSettings flash={flash} />}
                      talentSimple={<TalentSimpleUserSettings flash={flash} />}
                    />
                  }
                  byPassFields={[`isClient`]}
                />
              }
            >
              <Route index element={<Navigate to={links.user_settings_account} replace />} />
              <Route path={links.user_settings_account} element={<AccountSettings />} />
              <Route
                path={links.user_settings_scheduling}
                element={
                  <ProtectedAdminRoute
                    element={<InterviewScheduling />}
                    byPassFields={[`canScheduleInterviews`]}
                    navigateTo={links.user_settings_account}
                  />
                }
              />
            </Route>
            <Route
              path={links.client_team_builder}
              element={
                <ProtectedAdminRoute
                  element={
                    <PortalPage
                      missionDev={<ClientTalentTeamBuilder />}
                      talentSimple={<TalentSimpleTalentTeamBuilder />}
                    />
                  }
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route
              path={links.featured_teams}
              element={
                <ProtectedAdminRoute
                  element={<PortalPage missionDev={<FeaturedTeams />} talentSimple={<TalentSimpleFeaturedTeams />} />}
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route
              path={links.featured_team(`:slug`)}
              element={
                <ProtectedAdminRoute
                  element={
                    <PortalPage
                      missionDev={<FeaturedTeamProfiles />}
                      talentSimple={<TalentSimpleFeaturedTeamProfiles />}
                    />
                  }
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route
              path={links.community}
              element={
                <PortalPage
                  missionDev={<ClientCommunity />}
                  talentSimple={<Navigate to={links.client_search} replace />}
                />
              }
            />
            <Route
              path={links.client_applications}
              element={
                <DesktopRoute
                  element={
                    <ProtectedAdminRoute
                      element={
                        <PortalPage missionDev={<ClientApplications />} talentSimple={<TalentSimpleApplications />} />
                      }
                      byPassFields={[`isClient`, `isShowcase`]}
                    />
                  }
                />
              }
            />
            <Route
              path={links.client_mission_board}
              element={
                <ProtectedAdminRoute
                  element={
                    <PortalPage missionDev={<ClientMissionBoard />} talentSimple={<TalentSimpleMissionBoard />} />
                  }
                  byPassFields={[`isClient`, `isShowcase`]}
                />
              }
            />
            <Route path={links.client_mission_role_applications(`:id`, `:roleId`)} element={<MissionRole />}>
              <Route
                path={links.client_mission_role_applications(`:id`, `:roleId`)}
                element={
                  <PortalPage
                    missionDev={<ClientMissionRoleApplications />}
                    talentSimple={<TalentSimpleMissionRoleApplications />}
                  />
                }
              />
            </Route>
            <Route
              path={links.client_profile}
              element={
                <ProtectedAdminRoute
                  element={<PortalPage missionDev={<ClientProfile />} talentSimple={<TalentSimpleProfile />} />}
                  byPassFields={[`isClient`]}
                />
              }
            />
            <Route
              path={links.book_meeting_link}
              element={<PortalPage missionDev={<BookMeeting />} talentSimple={<TalentSimpleBookMeeting />} />}
            />
            <Route path={links.home} element={<Home />} />
            <Route path={links.missions} element={<Missions />}>
              <Route path={links.missions} element={<ProtectedAdminRoute element={<MissionsHome />} />} />
              <Route path={links.roles} element={<ProtectedAdminRoute element={<Roles />} />} />
              <Route path={links.mission_board} element={<MissionBoard />} />
              <Route
                path={links.mission_applicants}
                element={
                  <DesktopRoute
                    element={
                      <ProtectedAdminRoute
                        element={<MissionsApplicants />}
                        navigateTo={links.missions}
                        byPassFields={[`isClient`]}
                      />
                    }
                  />
                }
              />
              <Route
                path={links.mission_create}
                element={<ProtectedAdminRoute element={<MissionNew />} navigateTo={links.missions} />}
              />
            </Route>
            <Route path={links.mission_applications} element={<MissionsApplications />} />
            <Route path={links.mission_application(`:id`)} element={<MissionsApplication />} />
            <Route path={links.mission(`:id`)} element={<MissionProvider />}>
              <Route path={links.mission(`:id`)} element={<Mission />} />
              <Route path={links.mission_role(`:id`, `:roleId`)} element={<MissionRole />}>
                <Route path={links.mission_role(`:id`, `:roleId`)} element={<Navigate to="details" />} />
                <Route path={links.mission_role_details(`:id`, `:roleId`)} element={<MissionRoleDetails />} />
                <Route
                  path={links.mission_role_recommendations(`:id`, `:roleId`)}
                  element={<MissionRoleRecommendations />}
                />
                <Route path={links.mission_role_applications(`:id`, `:roleId`)} element={<MissionRoleApplications />} />
                <Route path={links.mission_role_discussions(`:id`, `:roleId`)} element={<MissionRoleDiscussions />} />
                <Route path={links.mission_role_vetting(`:id`, `:roleId`)} element={<MissionRoleVetting />} />
                <Route
                  path={links.mission_role_timeline(`:id`, `:roleId`)}
                  element={<ProtectedAdminRoute element={<MissionRoleTimeline />} />}
                />
              </Route>
              <Route path={links.mission_edit(`:id`)} element={<ProtectedAdminRoute element={<MissionEdit />} />} />
            </Route>
            <Route path={links.squads} element={<DesktopRoute element={<Squads />} />}>
              <Route path={links.squads} element={<ProtectedAdminRoute element={<SquadsHome />} />} />
              <Route path={links.squad(`:id`)} element={<SquadProfile />} />
              <Route path={links.squad_effort_report(`:id`)} element={<EffortReport />} />
              <Route path={links.squad_pulse(`:id`)} element={<SquadPulse />} />
              <Route path={links.squad_engineering(`:id`)} element={<Engineering />}>
                <Route path={links.squad_engineering(`:id`)} element={<EngineeringCodeTelemetry />} />
                <Route
                  path={links.squad_engineering_code_telemetry_individual(`:id`, `:uuid`)}
                  element={<EngineeringCodeTelemetry />}
                />
                <Route path={links.squad_activity_report(`:id`)} element={<SquadActivityLog />} />
                <Route path={links.squad_activity_all_time(`:id`)} element={<SquadLeaderboard />} />
              </Route>
              <Route path={links.squad_daily_report(`:id`)} element={<DailyReport />} />
              <Route path={links.squad_settings(`:id`)} element={<SquadSettings />}>
                <Route path={links.squad_settings(`:id`)} element={<GeneralSettings />} />
                <Route path={links.squad_daily_report_settings(`:id`)} element={<DailyReportSettings />} />
                <Route path={links.squad_engineering_metrics_settings(`:id`)} element={<SignalSettings />} />
                <Route path={links.squad_signal_installation_settings(`:id`)} element={<Signal />} />
              </Route>
              <Route path={links.squad_activity_log(`:id`)} element={<SquadActivityLog />} />
              <Route path={links.squad_term_sheet(`:id`)} element={<LegacyTermSheet />} />
              <Route path={links.admin_squads_feedback(`:id`)} element={<ClientFeedback />} />
              <Route path={links.squad_create} element={<SquadNew />} />
              <Route path={links.squad_insights(`:id`)} element={<SquadInsights />} />
            </Route>
            <Route path={links.network} element={<Network />} />
            <Route path={links.private_profile(`:id`)} element={<ProfileProvider />}>
              <Route path={links.private_profile(`:id`)} element={<Profiles />} />
              <Route
                path={links.profile_code_challenges(`:id`)}
                element={<ProtectedAdminRoute element={<ProfileCodeChallenges />} />}
              />
              <Route
                path={links.profile_applications(`:id`)}
                element={<ProtectedAdminRoute element={<ProfileApplications />} />}
              />
              <Route
                path={links.profile_assignments(`:id`)}
                element={<ProtectedAdminRoute element={<ProfileAssignments />} />}
              />
              <Route path={links.profile_video_interviews(`:id`)} element={<ProfileVideoInterviews />} />
              <Route path={links.rate(`:id`)} element={<Rate />} />
              <Route path={links.timeline(`:id`)} element={<ProtectedAdminRoute element={<Timeline />} />} />
              <Route path={links.notes(`:id`)} element={<Notes />} />
              <Route path={links.settings(`:id`)} element={<ProfileSettings />} />
            </Route>
            <Route path={links.timecards} element={<TimeCard />} />
            <Route path={links.time_sheet} element={<DesktopRoute element={<Timesheet />} />} />
            <Route path={links.partners} element={<ProtectedAdminRoute element={<Partners />} />} />
            <Route path={links.platform_settings} element={<ProtectedAdminRoute element={<PlatformSettings />} />}>
              <Route path={links.platform_settings} element={<ProtectedAdminRoute element={<PlatformAccess />} />} />
              <Route
                path={links.settings_code_challenges}
                element={<ProtectedAdminRoute element={<SettingsCodeChallenges />} />}
              />
              <Route
                path={links.setting_mission_events}
                element={<ProtectedAdminRoute element={<MissionEvents />} />}
              />
              <Route path={links.settings_tag_manager} element={<ProtectedAdminRoute element={<TagManager />} />} />
              <Route
                path={links.settings_video_presentations}
                element={<ProtectedAdminRoute element={<VideoPresentationSettings />} />}
              />
              <Route
                path={links.settings_video_presentation(`:uuid`)}
                element={<ProtectedAdminRoute element={<VideoPresentationSetting />} />}
              />
              <Route
                path={links.settings_blueprints}
                element={<ProtectedAdminRoute element={<BlueprintSettings />} />}
              />
              <Route
                path={links.settings_blueprint(`:uuid`)}
                element={<ProtectedAdminRoute element={<BlueprintSetting />} />}
              />
            </Route>
            <Route path={links.dashboard_home} element={<ProtectedAdminRoute element={<Dashboard />} />}>
              <Route path={links.dashboard_home} element={<ProtectedAdminRoute element={<Overview />} />} />
              <Route
                path={links.dashboard_activity_logs}
                element={<ProtectedAdminRoute element={<DashboardActivityLogs />} />}
              />
            </Route>
            <Route path={links.dashboard(`:id`)} element={<ProtectedAdminRoute element={<DashboardTeamView />} />} />
            <Route
              path={links.dashboard_activity_log(`:id`)}
              element={<ProtectedAdminRoute element={<DashboardTeamActivityLog />} />}
            />
            <Route path={links.organizations} element={<ProtectedAdminRoute element={<Organizations />} />} />
            <Route
              path={links.organization_profile(`:id`)}
              element={
                <ProtectedAdminRoute
                  element={<OrganizationProfile />}
                  byPassFields={[`isSignalClient`, `isPrimaryContact`]}
                />
              }
            />
            <Route path={links.lists} element={<ProtectedAdminRoute element={<Lists />} />} />
            <Route path={links.list(`:slug`)} element={<ProtectedAdminRoute element={<List />} />} />
          </Route>
          <Route path="/termsheets" element={<TermSheets />} />
        </Route>
      </Route>
    </ReactRouterRoutes>
  );
};

Routes.propTypes = {
  flash: PropTypes.object,
};

// Function to check if the user has any of the specified roles
const hasAnyField = (fields, requiredFields) =>
  requiredFields.some((field) => Object.keys(fields).includes(field) && fields[field]);

/**
 * ProtectedAdminRoute
 * Route wrapper to protect admin routes
 *
 * @param {Any}     element
 * @param {String}  navigateTo
 * @param {Array}   byPassFields   // Optional override field
 * @returns
 */
const ProtectedAdminRoute = ({ element, navigateTo, byPassFields = [] }) => {
  const { isAdmin, ...rest } = useAuth();
  const links = useLinks();

  if (!isAdmin && (!byPassFields.length || !hasAnyField(rest, byPassFields))) {
    return <Navigate to={navigateTo || links.home} />;
  }

  return element;
};

ProtectedAdminRoute.propTypes = {
  element: PropTypes.any.isRequired,
  navigateTo: PropTypes.string,
  byPassFields: PropTypes.array,
};

/**
 * Desktop Route
 *
 * @param {Any}     element
 * @returns
 */
const DesktopRoute = ({ element }) => {
  const { isMobile } = useViewport();

  if (isMobile) {
    return <MobileMessage />;
  } else {
    return element;
  }
};

DesktopRoute.propTypes = {
  element: PropTypes.any.isRequired,
};

export default Routes;
